import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { Attachment } from "lib/build/attachments";
import {
	Event,
	EventQuery,
	mapEventsToAttachments,
	mapQueryToString
} from "lib/build/events";

import { TimelineItem } from "src/common/components/Timeline";
import { useFormatFsn } from "src/common/hooks/useFormatFsn";
import { useFormatName } from "src/common/hooks/useFormatName";
import { useKnownUsers } from "src/features/auth";
import { fetchEvents, refetchEvents } from "src/features/events/actions";
import { EventsState } from "src/features/events/types";
import { useOrgModParams } from "src/features/orgmod";
import { AppDispatch } from "src/store";

const emptyEventList: Event[] = [];

export function useFetchEvents(
	patientId: string
): (queries: EventQuery[]) => void {
	const dispatch: AppDispatch = useDispatch();
	const { org, mod } = useOrgModParams();
	return React.useCallback(
		(queries: EventQuery[]) => {
			dispatch(fetchEvents({ org, mod, patientId, queries }));
		},
		[org, mod, patientId, dispatch]
	);
}

export function useRefetchEvents(
	patientId: string
): (query: EventQuery) => void {
	const dispatch: AppDispatch = useDispatch();
	const { org, mod } = useOrgModParams();
	return React.useCallback(
		(query: EventQuery) => {
			dispatch(refetchEvents({ org, mod, patientId, query }));
		},
		[org, mod, patientId, dispatch]
	);
}

export function useSelectEvents(): EventsState {
	return useSelector(({ events }: { events: EventsState }) => events);
}

export function useEventsByQueries<T>(
	queries: EventQuery[]
): (Event<T> | undefined)[] {
	const queriesRef = React.useRef(queries);
	const { items } = useSelector(
		({ events }: { events: EventsState }) => events
	);
	const events = React.useMemo(
		() =>
			queriesRef.current.map(query => {
				const code = mapQueryToString(query);
				const events = code in items ? items[code].events : [];
				return events.length === 0 ? undefined : events[0];
			}),
		[items]
	);
	return events;
}

export function useLastEventByQuery<T>(
	query: EventQuery
): Event<T> | undefined {
	const queryRef = React.useRef(query);
	const { items } = useSelector(
		({ events }: { events: EventsState }) => events
	);
	const { events } = React.useMemo(
		() =>
			items[mapQueryToString(queryRef.current)] || {
				events: emptyEventList
			},
		[items]
	);
	return events[0];
}

export function useEventsByQuery<T>(query: EventQuery): Event<T>[] {
	const queryRef = React.useRef(query);
	const { items } = useSelector(
		({ events }: { events: EventsState }) => events
	);
	const { events } = React.useMemo(
		() =>
			items[mapQueryToString(queryRef.current)] || {
				events: emptyEventList
			},
		[items]
	);
	return events;
}

export function useLastEventValueByQuery<T>(query: EventQuery): T | undefined {
	const event = useLastEventByQuery<T>(query);
	if (event === undefined) {
		return;
	}
	const { value } = event;
	return value;
}

export function useTimelineEventsByQueries<T>(
	queries: EventQuery[]
): Event<T>[] {
	const queriesRef = React.useRef(queries);
	const { items } = useSelector(
		({ events }: { events: EventsState }) => events
	);
	const events = React.useMemo(
		() =>
			queriesRef.current.flatMap(query => {
				const code = mapQueryToString(query);
				return code in items ? items[code].events : [];
			}),
		[items]
	);
	return events;
}

export function useTimelineItemsByEvents(
	events: Event[]
): TimelineItem<{ attachments: Attachment[]; data: unknown }>[] {
	const users = useKnownUsers();
	const formatFsn = useFormatFsn();
	const formatName = useFormatName();
	return React.useMemo(
		() =>
			events.map(event => {
				const {
					interpretation,
					timestamp,
					value,
					deleted,
					modified,
					tagDetails,
					origin: { recordedBy: userId }
				} = event;
				const label = formatFsn(interpretation);
				let author: string | undefined;
				if (userId in users) {
					author = formatName(users[userId]);
				}
				const attachments = mapEventsToAttachments(event);
				return {
					label,
					value: { attachments, data: value },
					timestamp,
					author,
					deleted,
					modified,
					tags: tagDetails.map(formatFsn)
				};
			}),
		[events, users, formatFsn, formatName]
	);
}
